import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { ADMIN_MENU_ICONS_PROVIDER, AdminConfirmDialogHostComponent, AdminToastHostComponent } from '../../shared/admin';
import {
  AdminSidebarComponent,
  isAdminMenuGroup,
  type AdminMenuItem,
} from './admin-sidebar.component';
import { AdminTopbarComponent } from './admin-topbar.component';
import { buildPageTitle } from '../../shared/constants/site-meta';
import { PermissionService } from '../permissions/permission.service';
import {
  PLATFORM_SIDEBAR_MENU,
  isPlatformMenuGroup,
  isPlatformMenuSection,
  type PlatformMenuConfigItem,
} from '../permissions/platform-menu.registry';

@Component({
  selector: 'app-platform-layout',
  imports: [RouterOutlet, AdminSidebarComponent, AdminTopbarComponent, AdminToastHostComponent, AdminConfirmDialogHostComponent],
  standalone: true,
  providers: [ADMIN_MENU_ICONS_PROVIDER],
  templateUrl: './platform-layout.component.html',
})
export class PlatformLayoutComponent {
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);

  readonly currentUser = this.authService.currentUser;
  readonly sidebarCollapsed = signal(false);
  readonly mobileNavOpen = signal(false);

  readonly menu = computed(() => {
    // Depend on currentUser so permission changes recompute the sidebar.
    void this.currentUser();
    return this.filterMenuByPermissions(PLATFORM_SIDEBAR_MENU);
  });

  private readonly activeUrl = signal(this.router.url);

  readonly pageTitle = computed(() => {
    const url = this.activeUrl();
    if (url.includes('/countries/new')) return 'New country';
    if (url.includes('/countries/') && url.endsWith('/edit')) return 'Edit country';
    if (url.includes('/platform/blogs/new')) return 'New blog post';
    if (url.includes('/platform/blogs/') && url.endsWith('/edit')) return 'Edit blog post';
    if (url.includes('/franchises/new')) return 'New franchise';
    if (url.includes('/transitions')) return 'Configure Transitions';
    if (url.includes('/roles-permissions')) return 'Roles & Permissions';
    if (url.includes('/settings/release-notes')) return 'Release Notes';
    if (url.includes('/settings')) return 'Settings';
    const match = this.findMenuLabel(url);
    return match ?? 'Platform';
  });

  constructor() {
    effect(() => {
      this.title.setTitle(buildPageTitle(this.pageTitle()));
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.activeUrl.set(e.urlAfterRedirects));
  }

  private filterMenuByPermissions(items: PlatformMenuConfigItem[]): AdminMenuItem[] {
    const result: AdminMenuItem[] = [];
    for (const item of items) {
      if (isPlatformMenuSection(item)) {
        result.push({ section: item.section });
        continue;
      }
      if (isPlatformMenuGroup(item)) {
        const children = item.children.filter((c) =>
          this.permissionService.canAccessModule(c.moduleKey),
        );
        if (children.length === 0) continue;
        result.push({
          label: item.label,
          icon: item.icon,
          defaultExpanded: item.defaultExpanded,
          children: children.map((c) => ({ label: c.label, route: c.route })),
        });
        continue;
      }
      if (!this.permissionService.canAccessModule(item.moduleKey)) continue;
      result.push({ label: item.label, route: item.route });
    }
    return result.filter((item, i, arr) => {
      if (!('section' in item)) return true;
      const next = arr[i + 1];
      return next !== undefined && !('section' in next);
    });
  }

  private findMenuLabel(url: string): string | undefined {
    for (const item of this.menu()) {
      if ('section' in item) continue;
      if (isAdminMenuGroup(item)) {
        const child = item.children.find((c) => url === c.route || url.startsWith(c.route + '/'));
        if (child) return child.label;
      } else if (url === item.route || url.startsWith(item.route + '/')) {
        return item.label;
      }
    }
    return undefined;
  }

  toggleSidebar(): void {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      this.mobileNavOpen.update((v) => !v);
      return;
    }
    this.sidebarCollapsed.update((v) => !v);
  }

  closeMobileNav(): void {
    this.mobileNavOpen.set(false);
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }
}
